/**
 * Value-kind FieldTypes.
 *
 * Storage strategy: the raw value is stored directly, either on a fixed
 * top-level column of the row (`record.name`, `record.created_at`) or in the
 * row's jsonb data blob at `record.data[key]` for schema-defined fields. The
 * FieldType is indifferent to which — the collection write helpers partition
 * between the two.
 *
 * Includes top-level types (string, number, boolean, timestamp, select, object)
 * and their backed subtypes (markdown, integer, currency, percent, filter,
 * visibility). Data only: operator groups live in `../operators/value.ts`.
 */

import { formatSnakeCase } from "../../utils/stringFormatter.js"
import * as group from "../group.js"
import * as sort from "../sort.js"
import type { ValueFieldType } from "../types.js"

// Value-kind type names: top-level types whose `parent` is the `value` root,
// plus subtypes named for their immediate parent.
export type ValueType = "string" | "number" | "boolean" | "timestamp" | "select" | "object"
export type StringType = "markdown"
export type NumberType = "integer" | "currency" | "percent"
export type SelectType = "visibility" | "deal_status" | "task_status" | "review_status"
export type ObjectType = "filter"

const currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

/**
 * Renderable options for a select field, derived from its `values` — a list of
 * scalar choices, each labelled via `formatSnakeCase`. Non-scalar entries are
 * dropped.
 */
export function selectOptions(field: {
  values?: readonly unknown[] | null
}): { value: string | number; label: string }[] {
  if (!field.values) {
    console.warn(`Select field is missing values:`, field)
    return []
  }

  return field.values.map((v) => ({
    value: v as string | number,
    label: formatSnakeCase(String(v)),
  }))
}

// VALUE (root) — scalars stored directly in record.data. The empty-string → null
// coercion is generic ("blank means absent"); subtypes that need more (numeric
// coercion, date parsing) override `parse`. Standalone so the top-level types
// can spread it.
const VALUE: ValueFieldType = {
  name: "value",
  kind: "value",
  label: "Value",
  icon: "font",
  parse: (input) => (input === "" ? null : input),
}

// Top-level value types (the `value` root plus each concrete top-level type);
// subtypes are added below, spreading their parent entry. The literal key union
// forces every top-level type to be present and makes each a known property the
// subtype spreads read.
export const fieldTypes: Record<"value" | ValueType, ValueFieldType> = {
  value: VALUE,

  string: {
    ...VALUE,
    name: "string",
    label: "String",
    parent: "value",
    icon: "font",
    sort: sort.text,
    format: (value) => (value == null ? "" : String(value)),
    parse: (input) => (input == null ? null : String(input)),
  },
  number: {
    ...VALUE,
    name: "number",
    label: "Number",
    parent: "value",
    icon: "hashtag",
    sort: sort.numeric,
    format: (value) => (value == null || value === "" ? "" : String(value)),
    parse: (input) => {
      if (input == null || input === "") return null
      const n = typeof input === "number" ? input : Number(input)
      return Number.isNaN(n) ? null : n
    },
  },
  boolean: {
    ...VALUE,
    name: "boolean",
    label: "Boolean",
    parent: "value",
    icon: "toggle-on",
    sort: sort.boolean,
    ...group.byBoolean,
    format: (value) => (value ? "Yes" : "No"),
    parse: (input) => {
      if (typeof input === "boolean") return input
      if (input == null || input === "") return null
      if (typeof input === "string") {
        const lower = input.toLowerCase()
        if (["true", "yes", "1"].includes(lower)) return true
        if (["false", "no", "0"].includes(lower)) return false
      }
      return Boolean(input)
    },
  },
  timestamp: {
    ...VALUE,
    name: "timestamp",
    label: "Timestamp",
    parent: "value",
    icon: "clock",
    sort: sort.timestamp,
    format: (value) => {
      if (value == null || value === "") return ""
      const d = value instanceof Date ? value : new Date(value as string | number)
      return Number.isNaN(d.getTime()) ? "" : d.toISOString()
    },
    parse: (input) => {
      if (input == null || input === "") return null
      if (input instanceof Date) return input.toISOString()
      const d = new Date(input as string | number)
      return Number.isNaN(d.getTime()) ? null : d.toISOString()
    },
  },
  select: {
    ...VALUE,
    name: "select",
    label: "Select",
    parent: "value",
    icon: "location-arrow",
    sort: sort.option,
    ...group.byOption,
    requiredProps: ["values"],
    format: (value) => (value == null ? "" : formatSnakeCase(value as string | number)),
    // parse inherited from `value` root (empty → null); select stores raw values.
  },
  object: {
    ...VALUE,
    name: "object",
    label: "Object",
    parent: "value",
    icon: "code",
    format: (value) => (value == null ? "" : JSON.stringify(value)),
    parse: (input) => {
      if (input == null || input === "") return null
      if (typeof input === "object") return input
      if (typeof input === "string") {
        try {
          return JSON.parse(input)
        } catch {
          return null
        }
      }
      return null
    },
  },
}

// STRING subtypes — spread `fieldTypes.string`.
export const stringTypes: Record<StringType, ValueFieldType> = {
  markdown: {
    ...fieldTypes.string,
    name: "markdown",
    label: "Markdown",
    parent: "string",
    icon: "align-justify",
    // A markdown field holds a document, which has no meaningful order. It spreads
    // `string`, so `sort: undefined` clears the string ordering it would inherit.
    sort: undefined,
  },
}

// NUMBER subtypes — spread `fieldTypes.number`, override format/parse for the
// integer-truncation, currency, and percent variants.
export const numberTypes: Record<NumberType, ValueFieldType> = {
  integer: {
    ...fieldTypes.number,
    name: "integer",
    label: "Integer",
    parent: "number",
    parse: (input: unknown) => {
      if (input == null || input === "") return null
      const n = typeof input === "number" ? input : Number(input)
      return Number.isNaN(n) ? null : Math.trunc(n)
    },
  },
  currency: {
    ...fieldTypes.number,
    name: "currency",
    label: "Currency",
    parent: "number",
    icon: "dollar-sign",
    format: (value: unknown) =>
      value == null || value === "" ? "" : currencyFormatter.format(Number(value)),
    parse: (input: unknown) => {
      if (input == null || input === "") return null
      const stripped = typeof input === "string" ? input.replace(/[^0-9.-]/g, "") : input
      const n = Number(stripped)
      return Number.isNaN(n) ? null : n
    },
  },
  percent: {
    ...fieldTypes.number,
    name: "percent",
    label: "Percent",
    parent: "number",
    icon: "percent",
    format: (value: unknown) => (value == null || value === "" ? "" : `${value}%`),
    parse: (input: unknown) => {
      if (input == null || input === "") return null
      const stripped = typeof input === "string" ? input.replace(/%\s*$/, "") : input
      const n = Number(stripped)
      return Number.isNaN(n) ? null : n
    },
  },
}

// SELECT subtypes — fixed-vocabulary enums over `select`. Options come from
// these baked `values`; the frontend renders them as colored pill pickers.
export const selectTypes: Record<SelectType, ValueFieldType> = {
  visibility: {
    ...fieldTypes.select,
    name: "visibility",
    label: "Visibility",
    parent: "select",
    icon: "eye",
    values: ["private", "shared", "public"],
  },
  deal_status: {
    ...fieldTypes.select,
    name: "deal_status",
    label: "Deal Status",
    parent: "select",
    icon: "money-bill-trend-up",
    values: ["open", "won", "lost"],
  },
  task_status: {
    ...fieldTypes.select,
    name: "task_status",
    label: "Task Status",
    parent: "select",
    icon: "list-check",
    values: ["todo", "doing", "done", "dismissed"],
  },
  review_status: {
    ...fieldTypes.select,
    name: "review_status",
    label: "Review Status",
    parent: "select",
    icon: "clipboard-check",
    values: ["draft", "submitted", "approved", "rejected"],
  },
}

// OBJECT subtypes — filter targets records of the referenced type.
export const objectTypes: Record<ObjectType, ValueFieldType> = {
  filter: {
    ...fieldTypes.object,
    name: "filter",
    label: "Filter",
    parent: "object",
    icon: "filter",
  },
}

Object.assign(fieldTypes, stringTypes, numberTypes, selectTypes, objectTypes)
